import React, { useState } from "react";
import {
  Box,
  Button,
  Flex,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import plus from "../../assets/images/product/plus.svg";
import TableBrokerDebtCertificate from "./tableBrokerDebtCertificate";
import AddBrokerDebtCertificate from "./form/addBrokerDebtCertificate";
import { useBrokerDebtCertificate } from "../../hooks/useBrokerDebtCertificate";
import toast from "react-hot-toast";
import { brokerDebtCertificateCall } from "../../api/brokerDebtCertificate";
import IconFile from "../../assets/images/booking/upfile.svg";
import iconFile from "../../assets/images/apartment/Frame.svg";
import PopupImport from "../../components/popup/PopupImport";
import { exportExcelFromApi, exportExcelLocal } from "../../utils/tools";
import iconExcel from "../../assets/images/employee/iconExcel.svg";

/** Mapping cột Excel → field JS cho import CCMG tổng (per nhân viên) */
const columnMappingReverse = {
  "Mã nhân viên": "employeeCode",
  "Tên nhân viên": "employeeName",
  "Số tiền tạm giữ": "amountHeld",
  "Số tiền đã trả": "amountPaid",
  "Số tiền còn lại": "amountActualRemaining",
};

/**
 * Mapping cột Excel → field JS để kiểm tra dữ liệu BrokerDebtCertificate từ file export.
 * Headers khớp với file "Tạm giữ CCMG đến ..." được xuất từ hệ thống.
 * Chỉ dùng cho check-only (không ghi DB) — gọi endpoint /importCheck.
 */
const columnMappingReverseCheckOnly = {
  "Mã nhân viên":         "employeeCode",
  "Tên nhân viên":        "employeeName",
  "Số tiền tạm giữ CCMG": "amountHeld",
  "Đã hoàn":              "amountPaid",
  "Còn lại chưa hoàn":   "amountActualRemaining",
};

/**
 * Mapping cột Excel → field JS cho import CCMG theo đợt × căn × nhân viên.
 * Cả căn đã có PDV lẫn chưa có đều được import:
 *   - Có PDV: ghi đè IndividualServiceFee.certificateOrDeposit + sync ISFDetail.
 *   - Chưa có PDV: ghi thủ công vào BrokerDebtCertificatePeriod.
 *
 * "Đã hoàn" và "Còn lại chưa hoàn" là cột tuỳ chọn — không điền cũng được.
 * Nếu có, BE sẽ validate nhưng không lưu vào BrokerDebtCertificatePeriod
 * (model chỉ lưu amountHeld per đợt; amountPaid/amountActualRemaining tổng hợp
 * đã được xử lý bởi luồng import CCMG thường chạy trước).
 */
const columnMappingReverseByPeriod = {
  "Mã nhân viên":         "employeeCode",
  "Tên nhân viên":        "employeeName",
  "Mã căn":               "apartmentCode",
  "Tháng/Năm":            "monthYear",
  "Số tiền tạm giữ CCMG": "amountHeld",
  "Đã hoàn":              "amountPaid",            // tuỳ chọn — tham chiếu, không lưu vào period
  "Còn lại chưa hoàn":   "amountActualRemaining", // tuỳ chọn — tham chiếu, không lưu vào period
};

/**
 * Mapping cột Excel → field JS cho import thanh toán CCMG.
 * Bắt buộc: Ngày thanh toán, Mã nhân viên, Tên nhân viên, Số tiền thanh toán.
 * Tuỳ chọn: Tháng đối chiếu, Mã căn (chỉ để lưu vết nguồn số tiền — lịch sử).
 * Khoá ghi đè ở BE: (nhân viên × Ngày thanh toán).
 */
const columnMappingReverseDisbursement = {
  "Ngày thanh toán":    "disbursementDate",
  "Tháng đối chiếu":   "monthYear",
  "Mã căn":            "apartmentCode",
  "Mã nhân viên":      "employeeCode",
  "Tên nhân viên":     "employeeName",
  // Hai cột mới — chỉ lưu trữ, không tham gia tính toán (khách đã tính sẵn).
  "Số trước thuế":     "amountBeforeTax",
  "Thuế":              "taxAmount",
  "Số tiền thanh toán": "disbursedAmount",
};

export default function BrokerDebtCertificate() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isOpenConfirm,
    onOpen: onOpenConfirm,
    onClose: onCloseConfirm,
  } = useDisclosure();
  const [paramExport, setParamExport] = useState(null);
  const [popupVisible, setPopupVisible] = useState(false);
  // Popup import CCMG theo đợt × căn × nhân viên (tách biệt với import CCMG tổng)
  const [popupByPeriodVisible, setPopupByPeriodVisible] = useState(false);
  // Popup kiểm tra dữ liệu CCMG từ file export — chỉ check, không ghi DB
  const [popupCheckOnlyVisible, setPopupCheckOnlyVisible] = useState(false);
  // Popup import thanh toán CCMG (BrokerDebtDisbursement) — chọn mode ngay trong popup
  const [popupDisbursementVisible, setPopupDisbursementVisible] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const { refetch, isRefetching } = useBrokerDebtCertificate();

  const hasFilter = !!paramExport?.employeeId;

  const handleRecalculateAmountHeld = async (filterMode) => {
    onCloseConfirm();
    const isFiltered = filterMode === "filter";
    const label = isFiltered ? "theo filter hiện tại" : "tất cả nhân viên";
    const toastId = toast.loading(`Đang tính lại tạm giữ CCMG (${label})...`);
    setIsRecalculating(true);
    try {
      const params = isFiltered ? { employeeId: paramExport?.employeeId } : {};
      const res = await brokerDebtCertificateCall.recalculateAmountHeld(params);
      toast.dismiss(toastId);
      toast.success(
        `Tính lại thành công (${res?.data?.updatedCount ?? 0} nhân viên được cập nhật)`,
      );
      await refetch();
    } catch (error) {
      toast.dismiss(toastId);
    } finally {
      setIsRecalculating(false);
    }
  };

  const onSubmitAdd = async (data) => {
    const toastCreate = toast.loading(
      "Đang thêm công nợ chứng chỉ môi giới ...",
    );
    try {
      let filteredData = Object.fromEntries(
        Object.entries(data).filter(
          ([_, value]) => value !== "" && value !== undefined && value !== null,
        ),
      );
      await brokerDebtCertificateCall.create(filteredData);

      await refetch();
      onClose();
      toast.success("Thêm thành công");
    } catch (error) {
      console.log({ error });
      toast.dismiss(toastCreate);
    }
    toast.dismiss(toastCreate);
  };

  const handleExportSample = () => {
    const columnMapping = {
      employeeCode: "Mã nhân viên",
      employeeName: "Tên nhân viên",
      amountHeld: "Số tiền tạm giữ",
      amountPaid: "Số tiền đã trả",
      amountActualRemaining: "Số tiền còn lại",
    };

    const data = [
      {
        employeeCode: "MSH000321",
        employeeName: "Từ Tố Ngọc Linh",
        amountHeld: 30000000,
        amountPaid: 0,
        amountActualRemaining: 0,
      },
      {
        employeeCode: "CTV005094",
        employeeName: "Hoàng Hà Trang",
        amountHeld: 20000000,
        amountPaid: 10000000,
        amountActualRemaining: 0,
      },
      {
        employeeCode: "CTV005049",
        employeeName: "Trần Thị Thu Thảo",
        amountHeld: 30000000,
        amountActualRemaining: 0,
      },
      {
        employeeCode: "CTV005058",
        employeeName: "Vũ Thị Thùy Linh",
        amountPaid: 30000000,
        amountActualRemaining: 0,
      },
      {
        employeeCode: "CTV005066",
        employeeName: "Trần Ngọc Hiếu",
      },
    ];
    exportExcelLocal({
      data,
      mapping: columnMapping,
      filename: "mau_import_tam_giu_ccmg.xlsx",
      toastMessages: {
        success: "Download file thành công",
      },
    });
  };

  /**
   * Xuất file mẫu cho import CCMG theo đợt.
   * Cột bắt buộc: Mã nhân viên, Tên nhân viên, Mã căn, Tháng/Năm, Số tiền tạm giữ CCMG.
   * Cột tuỳ chọn: Đã hoàn, Còn lại chưa hoàn — có thể để trống, BE vẫn xử lý được.
   * Period suy ra tự động từ { lockingId, month, year } ở BE;
   * nếu chưa có StepCommission thì dùng synthetic period = year*100+month.
   */
  const handleExportSampleByPeriod = () => {
    const columnMapping = {
      employeeCode:          "Mã nhân viên",
      employeeName:          "Tên nhân viên",
      apartmentCode:         "Mã căn",
      monthYear:             "Tháng/Năm",
      amountHeld:            "Số tiền tạm giữ CCMG",
      amountPaid:            "Đã hoàn",
      amountActualRemaining: "Còn lại chưa hoàn",
    };

    const data = [
      {
        employeeCode:          "CTV005104",
        employeeName:          "Hoàng Thế Mạnh",
        apartmentCode:         "K0406",
        monthYear:             "11/2025",
        amountHeld:            8804717,
        amountPaid:            0,
        amountActualRemaining: 8804717,
      },
      {
        employeeCode:          "TEST1",
        employeeName:          "TESTER 1",
        apartmentCode:         "2002",
        monthYear:             "1/2025",
        amountHeld:            10000000,
        amountPaid:            0,
        amountActualRemaining: 10000000,
      },
      {
        employeeCode:          "TEST2",
        employeeName:          "TESTER2",
        apartmentCode:         "2003",
        monthYear:             "3/2025",
        amountHeld:            30000000,
        amountPaid:            0,
        amountActualRemaining: 30000000,
      },
    ];

    exportExcelLocal({
      data,
      mapping: columnMapping,
      filename: "mau_import_ccmg_theo_dot.xlsx",
      toastMessages: { success: "Download file mẫu thành công" },
    });
  };

  /**
   * Xuất file mẫu import thanh toán CCMG.
   * Cột bắt buộc: Ngày thanh toán, Mã nhân viên, Tên nhân viên, Số tiền thanh toán.
   * Cột tuỳ chọn: Tháng đối chiếu, Mã căn (để trống vẫn import được).
   */
  const handleExportSampleDisbursement = () => {
    const columnMapping = {
      disbursementDate: "Ngày thanh toán",
      monthYear: "Tháng đối chiếu",
      apartmentCode: "Mã căn",
      employeeCode: "Mã nhân viên",
      employeeName: "Tên nhân viên",
      // Hai cột mới — chỉ lưu trữ, không tính toán.
      amountBeforeTax: "Số trước thuế",
      taxAmount: "Thuế",
      disbursedAmount: "Số tiền thanh toán",
    };

    // Thứ tự key trong mỗi object quyết định thứ tự cột khi xuất → đặt
    // amountBeforeTax + taxAmount ngay trước disbursedAmount cho khớp file mẫu.
    const data = [
      {
        disbursementDate: "07/07/2026",
        monthYear: "01/2026",
        apartmentCode: "G.25.16",
        employeeCode: "CTV001257",
        employeeName: "Nguyễn Duy Cường",
        amountBeforeTax: 800000,
        taxAmount: 31686,
        disbursedAmount: 768314,
      },
      {
        disbursementDate: "07/07/2026",
        monthYear: "03/2026",
        apartmentCode: "G.25.16",
        employeeCode: "CTV001257",
        employeeName: "Nguyễn Duy Cường",
        amountBeforeTax: 800000,
        taxAmount: 31687,
        disbursedAmount: 768313,
      },
      {
        disbursementDate: "07/07/2026",
        employeeCode: "CTV001336",
        employeeName: "Bùi Ngọc Linh",
        amountBeforeTax: 66875730,
        taxAmount: 2667029,
        disbursedAmount: 64208701,
      },
    ];

    exportExcelLocal({
      data,
      mapping: columnMapping,
      filename: "mau_import_thanh_toan_ccmg.xlsx",
      // Tô vàng 2 cột mới để nổi bật (theo file mẫu khách).
      highlightHeaders: ["Số trước thuế", "Thuế"],
      toastMessages: { success: "Download file mẫu thành công" },
    });
  };

  const handleExport = async () => {
    const columnMapping = {
      employeeCode: "Mã nhân viên",
      fullName: "Tên nhân viên",
      amountHeld: "Số tiền tạm giữ",
      taxTNCN: "Thuế TNCN",
      amountPaid: "Số tiền đã trả",
      amountActualRemaining: "Số tiền còn lại",
    };

    await exportExcelFromApi({
      fetcher: () => brokerDebtCertificateCall.export(paramExport),
      mapping: columnMapping,
      filename: "bang_tam_giu_ccmg.xlsx",
      toastMessages: {
        loading: "Đang tải dữ liệu & xuất file...",
        success: "Xuất file thành công",
        noData: "Không có dữ liệu",
        error: "Xuất file thất bại",
      },
    });
  };
  return (
    <Box
      w={{ base: "100%" }}
      backgroundColor="var(--color-backgroundmain)"
      p={{ base: "24px 16px 16px" }}
    >
      <Flex alignItems={"center"} justifyContent={"space-between"}>
        <Flex alignItems={"center"} gap={{ base: "74px" }}>
          <Text fontSize={{ base: "20px" }} fontWeight={600}>
            Quản lý tạm giữ CCMG
          </Text>
        </Flex>
        <Flex alignItems={"center"} gap={"16px"} wrap={"wrap"}>
          {/* <Button
            h={"44px"}
            display={"flex"}
            alignItems={"center"}
            p={"10px 16px"}
            gap={"8px"}
            border={"1px solid var(--color-boder)"}
            backgroundColor={"var(--color-main)"}
            color={"var(--color-text-primary)"}
            _hover={{ filter: "brightness(95%)" }}
            isLoading={isRecalculating}
            loadingText="Đang tính lại..."
            onClick={onOpenConfirm}
          >
            <Text fontSize={{ base: "15px" }} fontWeight={600}>
              Tính lại tạm giữ CCMG
            </Text>
          </Button> */}

          <Button
            w={"246px"}
            h={"44px"}
            display={"flex"}
            alignItems={"center"}
            p={"10px 16px 10px 12px"}
            gap={"8px"}
            backgroundImage="var(--color-button)"
            _hover={{
              filter: "brightness(90%)",
            }}
            onClick={onOpen}
          >
            <Image src={plus} />
            <Text
              fontSize={{ base: "15px" }}
              fontWeight={600}
              color="var(--color-main)"
            >
              Tạo tạm giữ CCMG mới
            </Text>
          </Button>

          <Menu>
            <MenuButton
              h={"44px"}
              as={Button}
              backgroundImage="var(--color-button)"
              _hover={{ filter: "brightness(90%)" }}
              _active={{ filter: "brightness(85%)" }}
              rightIcon={<ChevronDownIcon color="var(--color-main)" />}
              px={"16px"}
            >
              <Flex alignItems={"center"} gap={"8px"}>
                <Image src={IconFile} />
                <Text
                  fontSize={{ base: "15px" }}
                  fontWeight={600}
                  color="var(--color-main)"
                >
                  Import / Kiểm tra CCMG
                </Text>
              </Flex>
            </MenuButton>
            <MenuList>
              <MenuItem
                onClick={() => setPopupVisible(true)}
                gap={"8px"}
                p={"10px 16px"}
                _hover={{ filter: "brightness(96%)" }}
              >
                <Image src={iconFile} /> Import tạm giữ CCMG
              </MenuItem>
              <MenuItem
                onClick={() => setPopupByPeriodVisible(true)}
                gap={"8px"}
                p={"10px 16px"}
                _hover={{ filter: "brightness(96%)" }}
              >
                <Image src={iconFile} /> Import CCMG theo đợt
              </MenuItem>
              <MenuItem
                onClick={() => setPopupDisbursementVisible(true)}
                gap={"8px"}
                p={"10px 16px"}
                _hover={{ filter: "brightness(96%)" }}
              >
                <Image src={iconFile} /> Import thanh toán CCMG
              </MenuItem>
              <MenuItem
                onClick={() => setPopupCheckOnlyVisible(true)}
                gap={"8px"}
                p={"10px 16px"}
                _hover={{ filter: "brightness(96%)" }}
              >
                <Image src={iconFile} /> Kiểm tra dữ liệu CCMG
              </MenuItem>
            </MenuList>
          </Menu>

          <Button
            display={"flex"}
            alignItems={"center"}
            p={"10px 16px 10px 12px"}
            gap={"8px"}
            h={"44px"}
            backgroundImage="var(--linear-gradient-excel)"
            _hover={{
              filter: "brightness(90%)",
            }}
            onClick={handleExport}
          >
            <Image src={iconExcel} />
            <Text
              fontSize={{ base: "15px" }}
              fontWeight={600}
              color="var(--color-main)"
            >
              Xuất thông tin tạm giữ CCMG
            </Text>
          </Button>
        </Flex>
      </Flex>
      <Box mt={{ base: "23px" }}>
        <TableBrokerDebtCertificate
          isRefetchingWithCreate={isRefetching}
          setParamExport={setParamExport}
        />
      </Box>

      {popupVisible && (
        <PopupImport
          isOpen={popupVisible}
          onClose={() => setPopupVisible(false)}
          refetchCheckInData={refetch}
          columnMappingReverse={columnMappingReverse}
          handleExport={handleExportSample}
          titleAdd="Thêm tạm giữ CCMG"
          inputAdd="Nhập danh sách tạm giữ CCMG"
          type={"importBrokerDebtDisbursement"}
          isTypePrice={true}
          requiredCheck={true}
        />
      )}

      {/* Popup import CCMG theo đợt × căn × nhân viên
          - type="importCcmgByPeriod": FE gọi importByPeriodCheck (dry-run) trước,
            sau khi pass mới gọi importByPeriod (ghi DB).
          - columnMappingReverse: mapping cột Excel → field của bảng mới BrokerDebtCertificatePeriod.
          - requiredCheck=true: bắt buộc kiểm tra dữ liệu trước khi import.
      */}
      {popupByPeriodVisible && (
        <PopupImport
          isOpen={popupByPeriodVisible}
          onClose={() => setPopupByPeriodVisible(false)}
          refetchCheckInData={refetch}
          columnMappingReverse={columnMappingReverseByPeriod}
          handleExport={handleExportSampleByPeriod}
          titleAdd="Import CCMG theo đợt"
          inputAdd="Nhập danh sách CCMG theo đợt và căn"
          type={"importCcmgByPeriod"}
          isTypePrice={true}
          requiredCheck={true}
          titleCheck="Kiểm tra dữ liệu CCMG"
        />
      )}

      {/* Popup import thanh toán CCMG (BrokerDebtDisbursement).
          - type="importCcmgDisbursement": FE gọi importCheck (dry-run) trước, pass mới gọi import.
          - Khoá ghi đè ở BE: (nhân viên × Ngày thanh toán). */}
      {popupDisbursementVisible && (
        <PopupImport
          isOpen={popupDisbursementVisible}
          onClose={() => setPopupDisbursementVisible(false)}
          refetchCheckInData={refetch}
          columnMappingReverse={columnMappingReverseDisbursement}
          handleExport={handleExportSampleDisbursement}
          titleAdd="Import thanh toán CCMG"
          inputAdd="Nhập danh sách thanh toán CCMG"
          type={"importCcmgDisbursement"}
          isTypePrice={true}
          requiredCheck={true}
          titleCheck="Kiểm tra dữ liệu thanh toán"
        />
      )}

      {/* Popup kiểm tra dữ liệu CCMG từ file export — không ghi DB.
          Dùng cùng endpoint /importCheck với import thường nhưng checkOnly=true
          nên nút "Thêm" sẽ không hiện ngay cả khi dữ liệu hợp lệ. */}
      {popupCheckOnlyVisible && (
        <PopupImport
          isOpen={popupCheckOnlyVisible}
          onClose={() => setPopupCheckOnlyVisible(false)}
          refetchCheckInData={refetch}
          columnMappingReverse={columnMappingReverseCheckOnly}
          handleExport={() => {}}
          titleAdd=""
          inputAdd="Kiểm tra dữ liệu CCMG"
          type={"importCcmgDataCheck"}
          isTypePrice={true}
          requiredCheck={true}
          checkOnly={true}
          titleCheck="Kiểm tra dữ liệu"
          isImport={true}
        />
      )}

      <AddBrokerDebtCertificate
        isOpen={isOpen}
        onClose={onClose}
        isEdit={false}
        onSubmit={onSubmitAdd}
      />

      {/* Confirm modal tính lại CCMG */}
      {/* <Modal isOpen={isOpenConfirm} onClose={onCloseConfirm} isCentered size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="16px" fontWeight={600}>
            Tính lại tạm giữ CCMG
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="14px" color="var(--color-text-secondary)" mb={4}>
              Thao tác này sẽ tính lại <strong>Số tiền tạm giữ</strong> dựa trên
              tổng CCMG đã giữ từ tất cả các căn trong hệ thống, ghi đè giá trị
              hiện tại.
            </Text>
            <Flex direction="column" gap={3}>
              {hasFilter && (
                <Button
                  w="100%"
                  h="44px"
                  backgroundImage="var(--color-button)"
                  _hover={{ filter: "brightness(90%)" }}
                  onClick={() => handleRecalculateAmountHeld("filter")}
                >
                  <Text fontSize="14px" fontWeight={600} color="var(--color-main)">
                    Tính lại theo bộc lọc hiện tại
                  </Text>
                </Button>
              )}
              <Button
                w="100%"
                h="44px"
                backgroundImage="var(--color-button)"
                _hover={{ filter: "brightness(90%)" }}
                onClick={() => handleRecalculateAmountHeld("all")}
              >
                <Text fontSize="14px" fontWeight={600} color="var(--color-main)">
                  Tính lại tất cả nhân viên
                </Text>
              </Button>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button
              h="40px"
              px={6}
              border="1px solid var(--color-boder)"
              backgroundColor="var(--color-main)"
              color="var(--color-text-primary)"
              _hover={{ filter: "brightness(95%)" }}
              onClick={onCloseConfirm}
            >
              Huỷ
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal> */}
    </Box>
  );
}
